'use client';

// React bindings for browser-side Dash0 identity (dash0-identity.ts):
// effect-scoped stamping with cleanup, shared by both routers so each
// identity concern has exactly one abstraction.

import { useEffect } from 'react';

import { addDash0IdentityAttributes, identifyDash0User } from './dash0-identity';
import { IdentityIds } from './identity-attributes';

type IdentifiableProfile = { id: string; guestId?: string | null };

/**
 * Attributes the Dash0 session and all browser signals to the logged-in
 * guest (`user.id` + numa.guest.id) while a profile is known. Deliberately
 * independent of the Segment identification paths: Dash0 is observability,
 * not consent-gated marketing analytics.
 */
export function useDash0UserIdentity(profile: IdentifiableProfile | null | undefined) {
  const profileId = profile?.id;
  const guestId = profile?.guestId ?? undefined;

  useEffect(() => {
    if (!profileId) return;
    return identifyDash0User({ profileId, guestId });
  }, [profileId, guestId]);
}

/**
 * Stamps the given identifiers on every browser signal while the owning
 * component is mounted, removing exactly those keys on unmount. Destructured
 * to primitive deps on purpose: callers often derive the ids from query data
 * that is a fresh object every poll, which would otherwise remove/re-add the
 * attributes on a timer.
 */
export function useDash0IdentityAttributes({ guestId, reservationId, propertyId }: Omit<IdentityIds, 'profileId'>) {
  useEffect(
    () => addDash0IdentityAttributes({ guestId, reservationId, propertyId }),
    [guestId, reservationId, propertyId],
  );
}
